import * as Constants from "./Constants";
import * as Globals from "./Globals";
import { Vector } from "./Vector";
import { Matrix } from "./Matrix";

export class Conic {

    readonly m_tp: number;
    readonly m_MDot: number;
    readonly m_e: number;
    readonly m_i: number;
    readonly m_ArgP: number;
    readonly m_Node: number;
    readonly m_rp: number;
    readonly m_OrbitToRefMatrix: Matrix;
    readonly m_a: number;
    readonly m_GM: number;
    readonly mRootEsq: number;

    static MAX_ECC_ELLIPSE = 1 - 0.00000001;
    static MIN_ECC_HYPERBOLA = 1 + 0.00000001;

    /// <summary>
    /// Generates a new Conic given the periapsis elements.
    /// </summary>
    /// <param name="periapsisRadius"></param>
    /// <param name="eccentricity"></param>
    /// <param name="inclination"></param>
    /// <param name="argumentOfPeriapsis"></param>
    /// <param name="ascendingNode"></param>
    /// <param name="mu"></param>
    /// <param name="tp"></param>
    /// <returns></returns>
    constructor(periapsisRadius: number, ecc: number, inclination: number, argumentOfPeriapsis: number, ascendingNode: number, mu: number, tp: number) {

        this.m_e = ecc;

        //the type of conic is determined by the eccentricity
        if (this.isEllipse) {
            // an ellipse
            this.m_a = periapsisRadius / (1 - ecc);

            //a precomputed value to speed computations later
            this.mRootEsq = Math.sqrt(1 - ecc * ecc);
        }
        else if (ecc >= Conic.MIN_ECC_HYPERBOLA) {
            // a hyperbola
            this.m_a = periapsisRadius / (1 - ecc);
            //a precomputed value to speed computations later
            this.mRootEsq = Math.sqrt(ecc * ecc - 1);
        }
        else {
            // a parabola
            this.m_a = 0;
            this.m_e = 1;
            //newConic = new ParabolicConic(periapsisRadius, tp, inclination, argumentOfPeriapsis, ascendingNode, mu);
        }

        this.m_tp = tp;
        this.m_GM = mu;
        this.m_i = inclination;
        this.m_ArgP = argumentOfPeriapsis;
        this.m_Node = ascendingNode;
        this.m_rp = periapsisRadius;


        if (!this.isParabola) {
            let a = this.m_a;
            this.m_MDot = Math.sqrt(mu / Math.abs(a * a * a));
        }

        //compute orbital to reference frame rotation matrix
        this.m_OrbitToRefMatrix = Matrix.fromRotationAboutAxis(2, ascendingNode).multiplyMatrix(Matrix.fromRotationAboutAxis(0, inclination)).multiplyMatrix(Matrix.fromRotationAboutAxis(2, argumentOfPeriapsis));
    }

    /** Construct from Kepler orbital elements */
    public static fromKeplerElements(semiMajorAxis: number, eccentricity: number, inclination: number, argumentOfPeriapsis: number, ascendingNode: number, meanAnomaly: number, mu: number, t: number): Conic {

        let rp = semiMajorAxis * (1 - eccentricity);

        //compute mean motion
        let MDot = Math.sqrt(mu / (semiMajorAxis * semiMajorAxis * Math.abs(semiMajorAxis)));

        // Compute time of periapsis passage
        let tp = t - 1000 * meanAnomaly / MDot;

        return new Conic(rp, eccentricity, inclination, argumentOfPeriapsis, ascendingNode, mu, tp);
    }

    /// <summary>
    /// Construct a new Conic from the given cartesian state vector.
    /// </summary>
    /// <param name="Position"></param>
    /// <param name="Velocity"></param>
    /// <param name="TheEpoch"></param>
    /// <param name="mu"></param>
    /// <returns>A new conic object.</returns>
    /// <remarks></remarks>
    static fromCartesianState(pos: Vector, vel: Vector, t: number, mu: number): Conic {

        // Compute distance from centre of central body
        let r = pos.magnitude;

        // Square of velocity magnitude
        let vsq = vel.magnitudeSquared;

        // Angular momentum vector, and magnitude
        let angMom = pos.vectorProduct(vel);
        let hsq = angMom.magnitudeSquared;
        let h = Math.sqrt(hsq);

        // Compute inclination
        let inc = 0;
        let cosi = angMom.z / h;
        let sini = Math.sqrt(1 - cosi * cosi);
        if (!(sini == 0 && cosi == 0)) {
            inc = Math.atan2(sini, cosi);
            if (inc < 0) {
                inc += Constants.TWOPI;
            }
        }

        // Compute semi-major axis (negative for hyperbolic orbits)
        let ainv = 2 / r - vsq / mu;
        let a = 1 / ainv;

        // Compute eccentricity (>1 for hyperbolic orbits)
        let esq = 1 - hsq * ainv / mu;
        let ecc = Math.sqrt(esq);

        // Compute the Right Ascension of the ascending node
        let node = 0;
        let hsi = h * sini;
        let sinn = angMom.x / hsi;
        let cosn = -angMom.y / hsi;
        if (!(sinn == 0 && cosn == 0)) {
            node = Math.atan2(sinn, cosn);
            if (node < 0) {
                node += Constants.TWOPI;
            }
        }

        // Compute true anomaly
        let nu = 0;
        let p = a * (1 - esq);
        let rrdot = pos.dotProduct(vel);
        if (ecc != 0) {
            let per = p / (ecc * r);
            let snu = rrdot * per / h;
            let cnu = per - 1 / ecc;
            nu = Math.atan2(snu, cnu);
            if (nu < 0) {
                nu += Constants.TWOPI;
            }
        }

        // Compute argument of periapsis
        let cu = (pos.x * cosn + pos.y * sinn) / r;
        let su = ((pos.y * cosn - pos.x * sinn) * cosi + pos.z * sini) / r;
        let u = Math.atan2(su, cu);
        if (u < 0.0) {
            u += Constants.TWOPI;
        }
        let argP = u - nu;
        if (argP < 0.0) {
            argP += Constants.TWOPI;
        }

        //get mean anomaly, from true anomaly
        let M = 0;
        if (ecc < 1) {
            let cea = Math.cos(nu) + ecc;
            let sea = Math.sin(nu) * Math.sqrt(1 - ecc * ecc);
            let ea = Math.atan2(sea, cea);
            M = ea - ecc * Math.sin(ea);
            if (M < 0) {
                M += Constants.TWOPI;
            }
        }
        else {
            let tH = (Math.sin(nu) * Math.sqrt(ecc * ecc - 1)) / (Math.cos(nu) + ecc);
            let ha = 0.5 * Math.log((1 + tH) / (1 - tH)); // Inverse tanh
            M = ecc * Math.sinh(ha) - ha;
        }

        return Conic.fromKeplerElements(a, ecc, inc, argP, node, M, mu, t);
    }

    /** gets the position as a function of time */
    getPos(t: number): Vector {
        let pos = this.getPosVelInOrbitalCoords(t)[0];
        return this.m_OrbitToRefMatrix.multiplyVector(pos);
    }

    /** gets the velocity as a function of time */
    getVel(t: number): Vector {
        let vel = this.getPosVelInOrbitalCoords(t)[1];
        return this.m_OrbitToRefMatrix.multiplyVector(vel);
    }

    /** Gets the period of the orbit, for non-elliptical orbits returns NaN */
    get period(): number {
        if (!this.isEllipse)
            return NaN;
        else
            return 1000 * Constants.TWOPI / this.m_MDot;
    }

    /** Returns true if the conic is an elliptical orbit */
    get isEllipse(): boolean {
        return (this.m_e <= Conic.MAX_ECC_ELLIPSE);
    }

    /** Returns true if the conic is a hyperbolic orbit */
    get isHyperbola(): boolean {
        return (this.m_e >= Conic.MIN_ECC_HYPERBOLA);
    }

    /** Returns true if the conic is a parabolic orbit */
    get isParabola(): boolean {
        return (!this.isEllipse && !this.isHyperbola);
    }

    private mean_to_True(M: number): number {
        //
        //Compute true anomaly from mean anomaly for parabolic orbits
        //
        let lastt = 0;

        // first guess
        let t: number = (this.m_e > 0.8 ? Math.PI : M);

        // not very good at convergence, typically takes 40 iterations !
        for (let i = 1; i <= 30; i++) {
            let temp = 6 * M - 3 * t;
            let negative = (temp < 0.0);
            temp = Math.abs(temp);
            t = Math.pow(temp, 1.0 / 3.0) * (negative ? - 1 : 1);
            let dt = Math.abs(lastt - t);
            if (dt < 0.00000001) {
                break;
            }
            lastt = t;
        }

        return 2 * Math.atan(t);
    }

    /** Gets a unit vector pointing towards the orbit pole */
    get orbitPole(): Vector {
        return this.m_OrbitToRefMatrix.multiplyVector(new Vector(0, 0, 1));
    }


    /** Gets the apoapsis distance in km. For non-elliptical orbits returns NaN */
    get ApoapsisDistance(): number {
        if (!this.isEllipse)
            return NaN;
        else
            return this.m_a * (1 + this.m_e);
    }

    /** Gets the position and velocity coordinates in the orbital reference plane */
    private getPosVelInOrbitalCoords(t: number): [Vector, Vector] {

        if (this.isEllipse) {
            let M = this.meanAnomaly(t);

            // Compute the eccentric anomaly by iteration
            let E = (this.m_e > 0.8 ? Math.PI : M)
            for (let i = 0; i < 20; i++) {
                let f = E - this.m_e * Math.sin(E) - M;
                E -= f / (1 - this.m_e * Math.cos(E));
                if (Math.abs(f) < 1e-10)
                    break;
            }

            let pos = new Vector(this.m_a * (Math.cos(E) - this.m_e), this.m_a * this.mRootEsq * Math.sin(E), 0);
            let c = Math.cos(E);
            let f = this.m_a * this.m_MDot / (1 - this.m_e * c);
            let vel = new Vector(-Math.sin(E) * f, this.mRootEsq * c * f, 0);
            return [pos, vel];
        }
        if (this.isHyperbola) {
            let M = this.meanAnomaly(t);

            // compute the hyperbolic anomaly by iteration
            let H = Math.log(2 * Math.abs(M) / this.m_e + 1.8);
            if (M < 0.0)
                H = -H;
            for (let i = 0; i < 20; i++) {
                let f = this.m_e * Globals.sinh(H) - H - M;
                H -= f / (this.m_e * Globals.cosh(H) - 1);
                if (Math.abs(f) < 1e-10)
                    break;
            }

            let s = Globals.sinh(H);
            let c = Globals.cosh(H);
            let f = this.m_a * this.m_MDot / (this.m_e * c - 1);

            let pos = new Vector(this.m_a * (c - this.m_e), -this.m_a * this.mRootEsq * s, 0);
            let vel = new Vector(s * f, -this.mRootEsq * c * f, 0);
            return [pos, vel];
        }
    }

    /** Compute the mean anomaly, given the time since periapsis */
    private meanAnomaly(TheTime: number): number {
        let ElapsedTime = (TheTime - this.m_tp) / 1000;
        let M = ElapsedTime * this.m_MDot;

        if (this.m_e < 1.0) {
            // Make sure it lies in the range -PI to PI
            M = Globals.reduceAngleTwoPi(M);
            if (M > Math.PI)
                M -= Constants.TWOPI;
        }

        return M;
    }

}



// WEBPACK FOOTER //
// ./src/Conic.ts